"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { IconCalendarEvent, IconMapPin } from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import type { EventProgram } from "./types";
import { SectionLink, UI, useText } from "./shared";

const SLIDE_MS = 6500;

export function EventHero({
  program,
  firstDayId,
}: {
  program: EventProgram;
  firstDayId: string;
}) {
  const { text, isKhmer } = useText();
  const [active, setActive] = useState(0);
  const slides = program.heroImages;

  useEffect(() => {
    if (slides.length < 2) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible")
        setActive((i) => (i + 1) % slides.length);
    }, SLIDE_MS);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  return (
    <section
      aria-labelledby="event-hero-title"
      className="event-dark relative isolate flex min-h-[clamp(600px,82svh,880px)] items-center overflow-hidden bg-[#03102d]"
    >
      {slides.map((src, i) => (
        <div
          key={src}
          aria-hidden="true"
          className={cn(
            "absolute inset-0 transition-opacity duration-[1400ms] ease-out",
            i === active ? "opacity-100" : "opacity-0",
          )}
        >
          {(i === 0 || i === active) && (
            <Image
              src={src}
              alt=""
              fill
              priority={i === 0}
              loading={i === 0 ? undefined : "lazy"}
              sizes="100vw"
              quality={75}
              className={cn(
                "object-cover object-center",
                i === active &&
                  "motion-safe:animate-[event-hero-zoom_9s_ease-out_forwards]",
              )}
            />
          )}
        </div>
      ))}
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(3,16,45,0.9),rgba(6,24,62,0.62)_55%,rgba(6,24,62,0.15))]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(6,24,62,0.15),rgba(3,16,45,0.65))]" />

      <div className="relative mx-auto w-full max-w-[1280px] px-5 pb-20 pt-20 sm:px-6 md:py-32">
        <h1
          id="event-hero-title"
          className={cn(
            "max-w-4xl text-[2.25rem] font-black leading-[1.5] text-white drop-shadow-[0_4px_24px_rgba(0,0,0,0.3)] sm:text-5xl md:text-6xl",
            !isKhmer && "sm:max-w-3xl sm:leading-[1.08] lg:text-[5.5rem]",
          )}
        >
          {text(program.name)}
        </h1>
        <div className="mt-7 flex max-w-3xl flex-col items-start gap-3 text-base leading-7 text-white/90 md:text-lg">
          <p className="inline-flex items-center gap-2 font-semibold text-[#f6cf63]">
            <IconCalendarEvent className="size-5" aria-hidden="true" />
            {text(program.dateRange)}
          </p>
          <p className="inline-flex items-center gap-2">
            <IconMapPin className="size-5 text-[#f6cf63]" aria-hidden="true" />
            {text(program.venue)} • {text(program.city)}
          </p>
        </div>
        <div className="mt-9 flex flex-col items-start gap-3 sm:flex-row">
          <SectionLink
            to="event-overview"
            className="inline-flex w-full items-center justify-center rounded-full bg-[#f6cf63] px-7 py-3 font-bold text-[#041127] shadow-lg transition hover:bg-[#f0bd3a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:w-auto"
          >
            {text(UI.exploreEvent)}
          </SectionLink>
          <SectionLink
            to={firstDayId}
            className="inline-flex w-full items-center justify-center rounded-full border border-white/30 bg-white/10 px-7 py-3 font-semibold text-white backdrop-blur transition hover:border-white/60 hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6cf63] sm:w-auto"
          >
            {text(UI.viewProgram)}
          </SectionLink>
        </div>
      </div>

    </section>
  );
}
